import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../utils/api';

type SocialKeyType = 'user' | 'worker' | 'admin';

interface SocialLimit {
  id: number;
  platform: string;
  creditLimit: number;
  creditUsed: number;
  isEnabled: boolean;
  allowedServices: string[] | null;
  remaining: number;
}

interface SocialKey {
  id: number;
  keyName: string;
  keyType: SocialKeyType;
  workerLabel: string | null;
  apiKey: string;
  rateLimit: number;
  isActive: boolean;
  lastUsed: string | null;
  createdAt: string;
  expiresAt: string | null;
  deviceId: string | null;
  allowedDomain: string | null;
  limits: SocialLimit[];
  requestCount: number;
}

interface MetricConfig {
  id: number;
  platform: string;
  service: string;
  creditCost: number;
  status: string;
  supportsCache: boolean;
  supportsFreshCheck: boolean;
  publicMessage?: string | null;
}

interface SocialCountLogStep {
  id: number;
  level: 'info' | 'warn' | 'error' | string;
  step: string;
  source: string | null;
  elapsedMs: number | null;
  details: Record<string, any>;
  createdAt: string;
}

interface SocialCountRequestLogRow {
  id: string;
  keyId: number | null;
  keyName: string | null;
  note: string | null;
  platform: string | null;
  service: string | null;
  status: string;
  source: string | null;
  cached: boolean;
  charged: boolean;
  chargedCredits: number;
  creditCost: number;
  count: number | string | null;
  errorCode: string | null;
  errorMessage: string | null;
  createdAt: string;
  completedAt: string | null;
  steps: SocialCountLogStep[];
}

interface LogFilters {
  keyId: string;
  requestId: string;
  note: string;
  requestStatus: string;
}

interface PlatformLimitForm {
  platform: string;
  enabled: boolean;
  creditLimit: number;
  creditNoLimit: boolean;
  allServices: boolean;
  services: string[];
}

interface FormState {
  keyName: string;
  keyType: SocialKeyType;
  workerLabel: string;
  platformLimits: PlatformLimitForm[];
  rateLimit: number;
  rateNoLimit: boolean;
  expiryDays: number;
  expiryNoLimit: boolean;
  isActive: boolean;
  deviceId: string;
  allowedDomain: string;
}

const emptyForm: FormState = {
  keyName: '',
  keyType: 'user',
  workerLabel: '',
  platformLimits: [],
  rateLimit: 1000,
  rateNoLimit: false,
  expiryDays: 30,
  expiryNoLimit: false,
  isActive: true,
  deviceId: '',
  allowedDomain: ''
};

const platformLabels: Record<string, string> = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  x: 'X / Twitter',
  threads: 'Threads',
  telegram: 'Telegram'
};


const keyTypeLabels: Record<SocialKeyType, string> = {
  user: 'Kh\u00e1ch h\u00e0ng',
  worker: 'Worker',
  admin: 'Admin'
};

const serviceLabels: Record<string, string> = {
  followers: 'Follower',
  following: '\u0110ang theo d\u00f5i',
  posts: 'B\u00e0i vi\u1ebft',
  likes: 'Like',
  views: 'View',
  shares: 'Share',
  comments: 'B\u00ecnh lu\u1eadn',
  saves: 'L\u01b0u',
  subscribers: 'Subscriber',
  reposts: 'Repost',
  members: 'Th\u00e0nh vi\u00ean'
};

const statusLabels: Record<string, string> = {
  active: '\u0110ang h\u1ed7 tr\u1ee3',
  degraded: 'K\u00e9m \u1ed5n \u0111\u1ecbnh',
  maintenance: 'B\u1ea3o tr\u00ec',
  disabled: 'Ch\u01b0a tri\u1ec3n khai',
  deprecated: 'S\u1eafp ng\u1eebng'
};

const requestStatusLabels: Record<string, string> = {
  processing: '\u0110ang x\u1eed l\u00fd',
  completed: 'Ho\u00e0n th\u00e0nh',
  failed: 'Th\u1ea5t b\u1ea1i',
  cache_miss: 'Cache miss',
  rejected: 'T\u1eeb ch\u1ed1i'
};

const sourceLabels: Record<string, string> = {
  instagram_i_web_profile: 'Instagram i API',
  blastup: 'Blastup',
  instastatistics: 'Instastatistics',
  mixerno_facebook: 'Mixerno Facebook',
  tiktok_web: 'TikTok web',
  tokcounter: 'TokCounter',
  on4t: 'On4T'
};

const formatPlatformName = (platform: string) => platformLabels[platform] || platform;
const formatServiceName = (service: string) => serviceLabels[service] || service;
const formatStatusName = (status: string) => statusLabels[status] || status;
const formatRequestStatusName = (status: string) => requestStatusLabels[status] || status;
const formatSourceName = (source: string) => sourceLabels[source] || source;
const formatKeyTypeName = (type?: SocialKeyType) => keyTypeLabels[type || 'user'] || type || 'Kh\u00e1ch h\u00e0ng';
const getKeyTypeBadgeStyle = (type?: SocialKeyType): React.CSSProperties => ({
  ...statusBadgeStyle,
  backgroundColor: type === 'admin' ? '#fef3c7' : type === 'worker' ? '#e0f2fe' : '#dcfce7',
  color: type === 'admin' ? '#92400e' : type === 'worker' ? '#075985' : '#166534'
});


const ToggleSwitch: React.FC<{ checked: boolean; disabled?: boolean; onChange: (value: boolean) => void; label?: string }> = ({ checked, disabled, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      border: 'none',
      background: 'transparent',
      padding: 0,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.55 : 1,
      fontSize: '13px',
      fontWeight: 600,
      color: '#374151'
    }}
  >
    <span style={{
      width: '38px',
      height: '22px',
      borderRadius: '999px',
      position: 'relative',
      flexShrink: 0,
      backgroundColor: checked ? '#2563eb' : '#cbd5e1',
      transition: 'background-color 0.15s ease'
    }}>
      <span style={{
        position: 'absolute',
        top: '2px',
        left: checked ? '18px' : '2px',
        width: '18px',
        height: '18px',
        borderRadius: '50%',
        backgroundColor: 'white',
        boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
        transition: 'left 0.15s ease'
      }} />
    </span>
    {label ? <span>{label}</span> : null}
  </button>
);

const SocialCountKeys: React.FC = () => {
  const { hasAnyPermission, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [keys, setKeys] = useState<SocialKey[]>([]);
  const [configs, setConfigs] = useState<MetricConfig[]>([]);
  const [configSavingKey, setConfigSavingKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showAddService, setShowAddService] = useState(false);
  const [addServiceForm, setAddServiceForm] = useState({ platform: '', service: '', creditCost: 1, status: 'active', supportsCache: true, supportsFreshCheck: true });
  const [editingKey, setEditingKey] = useState<SocialKey | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [newApiKey, setNewApiKey] = useState('');
  const [newApiKeyTitle, setNewApiKeyTitle] = useState('');
  const [regeneratingKeyId, setRegeneratingKeyId] = useState<number | null>(null);
  const [visibleKeys, setVisibleKeys] = useState<Set<number>>(new Set());
  const [showLogModal, setShowLogModal] = useState(false);
  const [logRows, setLogRows] = useState<SocialCountRequestLogRow[]>([]);
  const [logLoading, setLogLoading] = useState(false);
  const [logError, setLogError] = useState('');
  const [logPage, setLogPage] = useState(1);
  const [logHasNext, setLogHasNext] = useState(false);
  const [logFilters, setLogFilters] = useState<LogFilters>({ keyId: '', requestId: '', note: '', requestStatus: '' });

  const visibleLogRows = logRows;

  const servicesByPlatform = useMemo(() => {
    const map = new Map<string, MetricConfig[]>();
    configs.forEach(config => {
      const services = map.get(config.platform) || [];
      services.push(config);
      map.set(config.platform, services);
    });
    return map;
  }, [configs]);

  const supportedPlatforms = useMemo(() => {
    const platforms = Array.from(servicesByPlatform.keys());
    return platforms.length > 0 ? platforms : ['instagram'];
  }, [servicesByPlatform]);

  const buildPlatformLimitForms = (key?: SocialKey | null): PlatformLimitForm[] => {
    const limitsByPlatform = new Map((key?.limits || []).map(limit => [limit.platform, limit]));

    return supportedPlatforms.map((platform, index) => {
      const existing = limitsByPlatform.get(platform);
      const services = (servicesByPlatform.get(platform) || []).map(config => config.service);
      const allowedServices = existing?.allowedServices || null;

      return {
        platform,
        enabled: existing ? existing.isEnabled : !key && index === 0,
        creditLimit: existing?.creditLimit === -1 ? 1000 : existing?.creditLimit || 1000,
        creditNoLimit: existing?.creditLimit === -1,
        allServices: !allowedServices || allowedServices.length === 0,
        services: allowedServices && allowedServices.length > 0 ? allowedServices : services
      };
    });
  };

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 4500);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');

      const [keysResponse, configsResponse] = await Promise.all([
        apiRequest('/api/social-count-management/keys'),
        apiRequest('/api/social-count-management/configs')
      ]);

      if (!keysResponse.ok) {
        const data = await keysResponse.json();
        throw new Error(data.error || 'Kh\u00f4ng th\u1ec3 t\u1ea3i danh s\u00e1ch key check count');
      }

      const keysData = await keysResponse.json();
      setKeys(keysData.data || []);

      if (configsResponse.ok) {
        const configsData = await configsResponse.json();
        setConfigs(configsData.data || []);
      }

    } catch (err: any) {
      setError(err.message || 'L\u1ed7i k\u1ebft n\u1ed1i server');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const resetForm = () => {
    setEditingKey(null);
    setNewApiKey('');
    setNewApiKeyTitle('');
    setForm({
      ...emptyForm,
      platformLimits: buildPlatformLimitForms()
    });
  };

  const openCreate = () => {
    resetForm();
    setShowModal(true);
  };

  const openEdit = (key: SocialKey) => {
    const expiresAt = key.expiresAt ? new Date(key.expiresAt) : null;
    const diffDays = expiresAt
      ? Math.max(1, Math.ceil((expiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
      : 30;

    setEditingKey(key);
    setNewApiKey('');
    setNewApiKeyTitle('');
    setForm({
      keyName: key.keyName,
      keyType: key.keyType || 'user',
      workerLabel: key.workerLabel || '',
      platformLimits: buildPlatformLimitForms(key),
      rateLimit: key.rateLimit === -1 ? 1000 : key.rateLimit || 1000,
      rateNoLimit: key.rateLimit === -1,
      expiryDays: diffDays,
      expiryNoLimit: !key.expiresAt,
      isActive: key.isActive,
      deviceId: key.deviceId || '',
      allowedDomain: key.allowedDomain || ''
    });
    setShowModal(true);
  };

  const updatePlatformLimit = (platform: string, updater: (limit: PlatformLimitForm) => PlatformLimitForm) => {
    setForm(prev => ({
      ...prev,
      platformLimits: prev.platformLimits.map(limit => limit.platform === platform ? updater(limit) : limit)
    }));
  };

  const toggleAllPlatforms = () => {
    const enabledCount = form.platformLimits.filter(limit => limit.enabled).length;
    const shouldEnable = enabledCount !== supportedPlatforms.length;

    setForm(prev => ({
      ...prev,
      platformLimits: prev.platformLimits.map(limit => ({ ...limit, enabled: shouldEnable }))
    }));
  };

  const togglePlatformService = (platform: string, service: string, checked: boolean) => {
    updatePlatformLimit(platform, limit => {
      const services = checked
        ? Array.from(new Set([...limit.services, service]))
        : limit.services.filter(item => item !== service);
      return { ...limit, services };
    });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!form.keyName.trim()) {
      showNotification('error', 'T\u00ean key l\u00e0 b\u1eaft bu\u1ed9c');
      return;
    }

    const enabledLimits = form.platformLimits.filter(limit => limit.enabled);
    if (enabledLimits.length === 0) {
      showNotification('error', 'Ch\u1ecdn \u00edt nh\u1ea5t m\u1ed9t n\u1ec1n t\u1ea3ng');
      return;
    }

    if (enabledLimits.some(limit => !limit.allServices && limit.services.length === 0)) {
      showNotification('error', 'N\u1ec1n t\u1ea3ng \u0111\u00e3 b\u1eadt ph\u1ea3i c\u00f3 \u00edt nh\u1ea5t m\u1ed9t d\u1ecbch v\u1ee5 ho\u1eb7c ch\u1ecdn t\u1ea5t c\u1ea3');
      return;
    }

    const payload = {
      keyName: form.keyName.trim(),
      keyType: form.keyType,
      workerLabel: form.keyType === 'user' ? '' : form.workerLabel.trim(),
      rateLimit: form.rateNoLimit ? -1 : Number(form.rateLimit),
      expiryDays: form.expiryNoLimit ? 0 : Number(form.expiryDays),
      deviceId: form.deviceId.trim(),
      allowedDomain: form.allowedDomain,
      isActive: form.isActive,
      platformLimits: form.keyType === 'user' ? enabledLimits.map(limit => ({
        platform: limit.platform,
        creditLimit: limit.creditNoLimit ? -1 : Number(limit.creditLimit),
        isEnabled: true,
        allowedServices: limit.allServices ? null : limit.services
      })) : []
    };

    try {
      const url = editingKey
        ? `/api/social-count-management/keys/${editingKey.id}`
        : '/api/social-count-management/keys';
      const response = await apiRequest(url, {
        method: editingKey ? 'PUT' : 'POST',
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (!response.ok) {
        const details = Array.isArray(data.details)
          ? data.details.map((item: any) => item.msg).join(', ')
          : '';
        throw new Error(details || data.error || 'Kh\u00f4ng th\u1ec3 l\u01b0u key');
      }

      if (!editingKey && data.data?.apiKey) {
        setNewApiKeyTitle('API key m\u1edbi');
        setNewApiKey(data.data.apiKey);
      } else {
        setShowModal(false);
        resetForm();
      }

      await fetchData();
      showNotification('success', editingKey ? '\u0110\u00e3 c\u1eadp nh\u1eadt key check count' : '\u0110\u00e3 t\u1ea1o key check count');
    } catch (err: any) {
      showNotification('error', err.message || 'L\u1ed7i k\u1ebft n\u1ed1i server');
    }
  };

  const updateServiceConfig = (platform: string, service: string, patch: Partial<MetricConfig>) => {
    setConfigs(prev => prev.map(config => (
      config.platform === platform && config.service === service
        ? { ...config, ...patch }
        : config
    )));
  };

  const handleSeedDefaults = async () => {
    if (!window.confirm('Thêm nhanh toàn bộ dịch vụ mặc định (Instagram/TikTok/Facebook/YouTube/X/Threads/Telegram)? Các dịch vụ đã có sẽ được giữ nguyên.')) {
      return;
    }

    try {
      const response = await apiRequest('/api/social-count-management/configs/seed-defaults', { method: 'POST' });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Không thể thêm nhanh dịch vụ');
      }

      await fetchData();
      const created = data.data?.metricCreated ?? 0;
      showNotification('success', created > 0 ? `Đã thêm ${created} dịch vụ mặc định` : 'Catalog đã đầy đủ, không có dịch vụ mới');
    } catch (err: any) {
      showNotification('error', err.message || 'Lỗi kết nối server');
    }
  };

  const handleAddService = async () => {
    const platform = addServiceForm.platform.trim().toLowerCase();
    const service = addServiceForm.service.trim().toLowerCase();
    if (!platform || !service) {
      showNotification('error', 'Nhập nền tảng và dịch vụ');
      return;
    }

    try {
      const response = await apiRequest(`/api/social-count-management/configs/${encodeURIComponent(platform)}/${encodeURIComponent(service)}`, {
        method: 'PUT',
        body: JSON.stringify({
          status: addServiceForm.status,
          creditCost: Number(addServiceForm.creditCost),
          supportsCache: Boolean(addServiceForm.supportsCache),
          supportsFreshCheck: Boolean(addServiceForm.supportsFreshCheck)
        })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Không thể thêm dịch vụ');
      }

      setShowAddService(false);
      setAddServiceForm({ platform: '', service: '', creditCost: 1, status: 'active', supportsCache: true, supportsFreshCheck: true });
      await fetchData();
      showNotification('success', 'Đã thêm dịch vụ');
    } catch (err: any) {
      showNotification('error', err.message || 'Lỗi kết nối server');
    }
  };

  const saveServiceConfig = async (config: MetricConfig) => {
    const saveKey = `${config.platform}:${config.service}`;
    try {
      setConfigSavingKey(saveKey);
      const response = await apiRequest(`/api/social-count-management/configs/${encodeURIComponent(config.platform)}/${encodeURIComponent(config.service)}`, {
        method: 'PUT',
        body: JSON.stringify({
          status: config.status,
          creditCost: Number(config.creditCost),
          supportsCache: Boolean(config.supportsCache),
          supportsFreshCheck: Boolean(config.supportsFreshCheck),
          publicMessage: config.publicMessage || null
        })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Kh\u00f4ng th\u1ec3 l\u01b0u tr\u1ea1ng th\u00e1i d\u1ecbch v\u1ee5');
      }

      const saved: MetricConfig = data.data;
      setConfigs(prev => prev.map(item => item.id === saved.id ? saved : item)
        .sort((a, b) => a.platform.localeCompare(b.platform) || a.service.localeCompare(b.service)));
      showNotification('success', '\u0110\u00e3 l\u01b0u tr\u1ea1ng th\u00e1i d\u1ecbch v\u1ee5');
    } catch (err: any) {
      showNotification('error', err.message || 'L\u1ed7i k\u1ebft n\u1ed1i server');
    } finally {
      setConfigSavingKey(null);
    }
  };

  const handleDeleteService = async (config: MetricConfig) => {
    const serviceName = `${formatPlatformName(config.platform)} / ${formatServiceName(config.service)}`;
    if (!window.confirm(`Xóa dịch vụ "${serviceName}"? Cấu hình liên quan của dịch vụ cũng sẽ bị xóa. Lịch sử request và cache cũ vẫn được giữ.`)) {
      return;
    }

    const saveKey = `${config.platform}:${config.service}`;
    try {
      setConfigSavingKey(saveKey);
      const response = await apiRequest(`/api/social-count-management/configs/${encodeURIComponent(config.platform)}/${encodeURIComponent(config.service)}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Không thể xóa dịch vụ');
      }

      setConfigs(prev => prev.filter(item => !(item.platform === config.platform && item.service === config.service)));
      showNotification('success', `Đã xóa dịch vụ ${serviceName}`);
    } catch (err: any) {
      showNotification('error', err.message || 'Lỗi kết nối server');
    } finally {
      setConfigSavingKey(null);
    }
  };
  const handleRegenerate = async (key: SocialKey) => {
    if (!window.confirm(`T\u1ea1o l\u1ea1i API key "${key.keyName}"? Key c\u0169 s\u1ebd kh\u00f4ng d\u00f9ng \u0111\u01b0\u1ee3c n\u1eefa.`)) {
      return;
    }

    try {
      setRegeneratingKeyId(key.id);
      const response = await apiRequest(`/api/social-count-management/keys/${key.id}/regenerate`, {
        method: 'POST'
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Kh\u00f4ng th\u1ec3 t\u1ea1o l\u1ea1i API key');
      }

      if (data.data?.apiKey) {
        setEditingKey(null);
        setNewApiKeyTitle('API key v\u1eeba t\u1ea1o l\u1ea1i');
        setNewApiKey(data.data.apiKey);
        setShowModal(true);
      }

      await fetchData();
      showNotification('success', '\u0110\u00e3 t\u1ea1o l\u1ea1i API key');
    } catch (err: any) {
      showNotification('error', err.message || 'L\u1ed7i k\u1ebft n\u1ed1i server');
    } finally {
      setRegeneratingKeyId(null);
    }
  };

  const handleDelete = async (key: SocialKey) => {
    if (!window.confirm(`X\u00f3a API key "${key.keyName}"?`)) {
      return;
    }

    try {
      const response = await apiRequest(`/api/social-count-management/keys/${key.id}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Kh\u00f4ng th\u1ec3 x\u00f3a key');
      }

      await fetchData();
      showNotification('success', '\u0110\u00e3 x\u00f3a key check count');
    } catch (err: any) {
      showNotification('error', err.message || 'L\u1ed7i k\u1ebft n\u1ed1i server');
    }
  };

  const copyToClipboard = async (text: string) => {
    await navigator.clipboard.writeText(text);
    showNotification('success', '\u0110\u00e3 sao ch\u00e9p API key');
  };

  const toggleKeyVisibility = (id: number) => {
    setVisibleKeys(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const fetchLogs = async (page = 1, filters = logFilters) => {
    try {
      setLogLoading(true);
      setLogError('');

      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', '50');
      if (filters.keyId) params.set('keyId', filters.keyId);
      if (filters.requestId.trim()) params.set('requestId', filters.requestId.trim());
      if (filters.note.trim()) params.set('note', filters.note.trim());
      if (filters.requestStatus) params.set('requestStatus', filters.requestStatus);

      const response = await apiRequest(`/api/social-count-management/request-logs?${params.toString()}`);
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Kh\u00f4ng th\u1ec3 t\u1ea3i l\u1ecbch s\u1eed log');
      }

      setLogRows(data.data || []);
      setLogPage(data.pagination?.page || page);
      setLogHasNext(Boolean(data.pagination?.hasNext));
    } catch (err: any) {
      setLogError(err.message || 'L\u1ed7i k\u1ebft n\u1ed1i server');
      setLogRows([]);
      setLogHasNext(false);
    } finally {
      setLogLoading(false);
    }
  };

  const openLogHistory = (key?: SocialKey) => {
    const nextFilters: LogFilters = {
      keyId: key ? String(key.id) : '',
      requestId: '',
      note: '',
      requestStatus: ''
    };

    setLogFilters(nextFilters);
    setLogRows([]);
    setLogError('');
    setLogPage(1);
    setLogHasNext(false);
    setShowLogModal(true);
    fetchLogs(1, nextFilters);
  };

  const submitLogFilters = (event: React.FormEvent) => {
    event.preventDefault();
    fetchLogs(1, logFilters);
  };

  const updateLogFilters = (patch: Partial<LogFilters>, loadNow = false) => {
    const nextFilters = { ...logFilters, ...patch };
    setLogFilters(nextFilters);
    if (loadNow) {
      fetchLogs(1, nextFilters);
    }
  };

  const formatLogTime = (value: string) => new Date(value).toLocaleString('vi-VN');

  const formatLogDetails = (details: Record<string, any>) => JSON.stringify(details || {}, null, 2);

  const getRequestStatusBadgeStyle = (status?: string | null): React.CSSProperties => {
    if (status === 'completed') {
      return { ...requestStatusBadgeStyle, backgroundColor: '#d1fae5', color: '#065f46' };
    }
    if (status === 'failed' || status === 'rejected') {
      return { ...requestStatusBadgeStyle, backgroundColor: '#fee2e2', color: '#991b1b' };
    }
    if (status === 'cache_miss') {
      return { ...requestStatusBadgeStyle, backgroundColor: '#fef3c7', color: '#92400e' };
    }
    return { ...requestStatusBadgeStyle, backgroundColor: '#dbeafe', color: '#1e40af' };
  };

  const formatLimit = (value: number) => value === -1 ? 'Kh\u00f4ng gi\u1edbi h\u1ea1n' : value.toLocaleString();
  const formatDate = (value: string | null) => value ? new Date(value).toLocaleString('vi-VN') : 'Kh\u00f4ng th\u1eddi h\u1ea1n';
  const formatServices = (services: string[] | null) => !services || services.length === 0
    ? 'T\u1ea5t c\u1ea3 d\u1ecbch v\u1ee5'
    : services.map(formatServiceName).join(', ');

  const renderServiceConfigPanel = () => {
    const groups = Array.from(configs.reduce((map, config) => {
      const current = map.get(config.platform) || [] as MetricConfig[];
      current.push(config);
      map.set(config.platform, current);
      return map;
    }, new Map<string, MetricConfig[]>()).entries())
      .sort(([a], [b]) => a.localeCompare(b));
    const canUpdate = hasPermission('social_count', 'update');
    const canDelete = hasPermission('social_count', 'delete');

    const applyServiceConfig = (config: MetricConfig, patch: Partial<MetricConfig>) => {
      updateServiceConfig(config.platform, config.service, patch);
      saveServiceConfig({ ...config, ...patch });
    };

    return (
      <section style={serviceConfigPanelStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '14px', flexWrap: 'wrap' }}>
          <div>
            <h2 style={{ margin: '0 0 6px', fontSize: '20px', color: '#111827' }}>Trạng thái dịch vụ Check Count</h2>
            <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>Mọi thay đổi được lưu tự động. Nếu dịch vụ tắt (disabled), API sẽ trả SERVICE_DISABLED.</p>
          </div>
          {canUpdate && (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button type="button" style={smallButtonStyle} onClick={handleSeedDefaults}>
                Thêm nhanh mặc định
              </button>
              <button type="button" style={smallButtonStyle} onClick={() => setShowAddService(value => !value)}>
                {showAddService ? 'Đóng' : '+ Thêm dịch vụ'}
              </button>
            </div>
          )}
        </div>

        {showAddService && canUpdate && (
          <div style={{ border: '1px dashed #cbd5e1', borderRadius: '8px', padding: '14px', marginBottom: '16px', background: '#f8fafc' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
              <label style={labelStyle}>Nền tảng
                <input value={addServiceForm.platform} onChange={e => setAddServiceForm({ ...addServiceForm, platform: e.target.value })} placeholder="vd: facebook" style={compactInputStyle} />
              </label>
              <label style={labelStyle}>Dịch vụ
                <input value={addServiceForm.service} onChange={e => setAddServiceForm({ ...addServiceForm, service: e.target.value })} placeholder="vd: followers" style={compactInputStyle} />
              </label>
              <label style={labelStyle}>Credit / req
                <input type="number" min={0} value={addServiceForm.creditCost} onChange={e => setAddServiceForm({ ...addServiceForm, creditCost: Number(e.target.value) })} style={compactInputStyle} />
              </label>
              <label style={labelStyle}>Trạng thái
                <select value={addServiceForm.status} onChange={e => setAddServiceForm({ ...addServiceForm, status: e.target.value })} style={compactInputStyle}>
                  <option value="active">Đang hỗ trợ</option>
                  <option value="degraded">Kém ổn định</option>
                  <option value="maintenance">Bảo trì</option>
                  <option value="disabled">Tắt</option>
                  <option value="deprecated">Sắp ngừng</option>
                </select>
              </label>
            </div>
            <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap', marginTop: '12px', alignItems: 'center' }}>
              <ToggleSwitch checked={addServiceForm.supportsFreshCheck} onChange={(value) => setAddServiceForm({ ...addServiceForm, supportsFreshCheck: value })} label={'Fresh check'} />
              <ToggleSwitch checked={addServiceForm.supportsCache} onChange={(value) => setAddServiceForm({ ...addServiceForm, supportsCache: value })} label={'Cache'} />
              <div style={{ flex: 1 }} />
              <button type="button" style={secondaryButtonStyle} onClick={() => setShowAddService(false)}>Hủy</button>
              <button type="button" style={primaryButtonStyle} onClick={handleAddService}>Thêm</button>
            </div>
          </div>
        )}

        {groups.length === 0 ? (
          <div style={{ color: '#6b7280', fontSize: '14px' }}>Chưa có cấu hình dịch vụ.</div>
        ) : (
          <div style={{ display: 'grid', gap: '18px' }}>
            {groups.map(([platform, services]) => (
              <div key={platform}>
                <h3 style={{ margin: '0 0 8px', fontSize: '15px', color: '#111827' }}>{formatPlatformName(platform)}</h3>
                <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '820px' }}>
                    <thead style={{ backgroundColor: '#f9fafb' }}>
                      <tr>
                        <th style={configThStyle}>Dịch vụ</th>
                        <th style={configThStyle}>Bật</th>
                        <th style={configThStyle}>Trạng thái chi tiết</th>
                        <th style={configThStyle}>Credit / req</th>
                        <th style={{ ...configThStyle, textAlign: 'center' }}>Fresh check</th>
                        <th style={{ ...configThStyle, textAlign: 'center' }}>Cache</th>
                        <th style={{ ...configThStyle, textAlign: 'center' }}>Thao tác</th>
                      </tr>
                    </thead>
                    <tbody>
                      {services.map(config => {
                        const saveKey = `${config.platform}:${config.service}`;
                        const saving = configSavingKey === saveKey;
                        const isCallable = config.status !== 'disabled' && config.supportsFreshCheck;
                        const rowDisabled = !canUpdate || saving;

                        return (
                          <tr key={saveKey} style={{ borderTop: '1px solid #e5e7eb', opacity: saving ? 0.6 : 1 }}>
                            <td style={configTdStyle}>
                              <div style={{ fontWeight: 600, color: '#111827' }}>{formatServiceName(config.service)}</div>
                              <span style={{ ...(isCallable ? enabledProviderBadgeStyle : disabledProviderBadgeStyle), display: 'inline-block', marginTop: '4px' }}>{isCallable ? 'Có thể gọi' : 'Chưa khả dụng'}</span>
                            </td>
                            <td style={configTdStyle}>
                              <ToggleSwitch
                                checked={config.status !== 'disabled'}
                                disabled={rowDisabled}
                                onChange={(value) => applyServiceConfig(config, { status: value ? 'active' : 'disabled' })}
                                label={config.status !== 'disabled' ? 'Bật' : 'Tắt'}
                              />
                            </td>
                            <td style={configTdStyle}>
                              <select
                                value={config.status}
                                disabled={rowDisabled}
                                onChange={e => applyServiceConfig(config, { status: e.target.value })}
                                style={compactInputStyle}
                              >
                                <option value="active">Đang hỗ trợ</option>
                                <option value="degraded">Kém ổn định</option>
                                <option value="maintenance">Bảo trì</option>
                                <option value="disabled">Tắt</option>
                                <option value="deprecated">Sắp ngừng</option>
                              </select>
                            </td>
                            <td style={configTdStyle}>
                              <input
                                type="number"
                                min={0}
                                value={config.creditCost}
                                disabled={rowDisabled}
                                onChange={e => updateServiceConfig(config.platform, config.service, { creditCost: Number(e.target.value) })}
                                onBlur={() => saveServiceConfig(config)}
                                style={{ ...compactInputStyle, width: '84px' }}
                              />
                            </td>
                            <td style={{ ...configTdStyle, textAlign: 'center' }}>
                              <ToggleSwitch
                                checked={config.supportsFreshCheck}
                                disabled={rowDisabled}
                                onChange={(value) => applyServiceConfig(config, { supportsFreshCheck: value })}
                              />
                            </td>
                            <td style={{ ...configTdStyle, textAlign: 'center' }}>
                              <ToggleSwitch
                                checked={config.supportsCache}
                                disabled={rowDisabled}
                                onChange={(value) => applyServiceConfig(config, { supportsCache: value })}
                              />
                            </td>
                            <td style={{ ...configTdStyle, textAlign: 'center' }}>
                              <button
                                type="button"
                                disabled={!canDelete || saving}
                                onClick={() => handleDeleteService(config)}
                                style={{
                                  ...smallButtonStyle,
                                  borderColor: '#fecaca',
                                  color: '#b91c1c',
                                  backgroundColor: '#fff7f7',
                                  opacity: !canDelete || saving ? 0.55 : 1
                                }}
                              >
                                Xóa
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    );
  };
  const renderPlatformLimits = () => {
    const enabledCount = form.platformLimits.filter(limit => limit.enabled).length;

    return (
      <div style={{ ...labelStyle, gridColumn: '1 / -1' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start' }}>
          <div>
            <div>{'N\u1ec1n t\u1ea3ng v\u00e0 d\u1ecbch v\u1ee5'}</div>
            <div style={{ color: '#6b7280', fontSize: '13px', fontWeight: 400, marginTop: '4px' }}>
              {'Ch\u1ecdn nh\u1eefng n\u1ec1n t\u1ea3ng key n\u00e0y \u0111\u01b0\u1ee3c ph\u00e9p g\u1ecdi. M\u1ed7i n\u1ec1n t\u1ea3ng c\u00f3 credit ri\u00eang v\u00e0 c\u00f3 th\u1ec3 ch\u1ecdn t\u1ea5t c\u1ea3 ho\u1eb7c t\u1eebng d\u1ecbch v\u1ee5.'}
            </div>
          </div>
          <button type="button" style={smallButtonStyle} onClick={toggleAllPlatforms}>
            {enabledCount === supportedPlatforms.length ? 'B\u1ecf ch\u1ecdn t\u1ea5t c\u1ea3' : 'Ch\u1ecdn t\u1ea5t c\u1ea3 n\u1ec1n t\u1ea3ng'}
          </button>
        </div>

        <div style={platformGridStyle}>
          {form.platformLimits.map(platformLimit => {
            const services = servicesByPlatform.get(platformLimit.platform) || [];

            return (
              <div key={platformLimit.platform} style={{ ...platformCardStyle, opacity: platformLimit.enabled ? 1 : 0.72 }}>
                <label style={{ ...checkStyle, fontWeight: 700, fontSize: '14px' }}>
                  <input
                    type="checkbox"
                    checked={platformLimit.enabled}
                    onChange={e => updatePlatformLimit(platformLimit.platform, limit => ({ ...limit, enabled: e.target.checked }))}
                  />
                  {'B\u1eadt n\u1ec1n t\u1ea3ng'}: {formatPlatformName(platformLimit.platform)}
                </label>

                <div style={platformControlsStyle}>
                  <label style={labelStyle}>
                    {'Gi\u1edbi h\u1ea1n credit'}
                    <input
                      type="number"
                      value={platformLimit.creditNoLimit ? '' : platformLimit.creditLimit}
                      disabled={!platformLimit.enabled || platformLimit.creditNoLimit}
                      onChange={e => updatePlatformLimit(platformLimit.platform, limit => ({ ...limit, creditLimit: Number(e.target.value) }))}
                      style={inputStyle}
                    />
                  </label>
                  <label style={{ ...checkStyle, alignSelf: 'end', paddingBottom: '10px' }}>
                    <input
                      type="checkbox"
                      checked={platformLimit.creditNoLimit}
                      disabled={!platformLimit.enabled}
                      onChange={e => updatePlatformLimit(platformLimit.platform, limit => ({ ...limit, creditNoLimit: e.target.checked }))}
                    />
                    {'Kh\u00f4ng gi\u1edbi h\u1ea1n'}
                  </label>
                </div>

                <div style={{ marginTop: '12px' }}>
                  <label style={checkStyle}>
                    <input
                      type="checkbox"
                      checked={platformLimit.allServices}
                      disabled={!platformLimit.enabled}
                      onChange={e => updatePlatformLimit(platformLimit.platform, limit => ({ ...limit, allServices: e.target.checked }))}
                    />
                    {'T\u1ea5t c\u1ea3 d\u1ecbch v\u1ee5'}
                  </label>

                  <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {services.length === 0 ? (
                      <span style={{ color: '#6b7280', fontSize: '13px' }}>{'Ch\u01b0a c\u00f3 d\u1ecbch v\u1ee5 h\u1ed7 tr\u1ee3'}</span>
                    ) : services.map(service => (
                      <label key={service.service} style={{ ...servicePillStyle, opacity: platformLimit.enabled && !platformLimit.allServices ? 1 : 0.55 }}>
                        <input
                          type="checkbox"
                          checked={platformLimit.services.includes(service.service)}
                          disabled={!platformLimit.enabled || platformLimit.allServices}
                          onChange={e => togglePlatformService(platformLimit.platform, service.service, e.target.checked)}
                        />
                        <span>{formatServiceName(service.service)}</span>
                        <span style={{ color: '#6b7280' }}>({service.creditCost} credit)</span>
                        {service.status !== 'active' && (
                          <span style={statusBadgeStyle}>{formatStatusName(service.status)}</span>
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (!hasAnyPermission('social_count')) {
    return (
      <Layout currentPage="social-count">
        <div style={{ padding: '24px' }}>
          <h1>{'Qu\u1ea3n l\u00fd Key API Check Count'}</h1>
          <p>{'B\u1ea1n kh\u00f4ng c\u00f3 quy\u1ec1n truy c\u1eadp trang n\u00e0y.'}</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout currentPage="social-count">
      <div style={{ padding: '24px', maxWidth: '1600px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <h1 style={{ margin: '0 0 8px 0', color: '#111827', fontSize: '28px' }}>{'Qu\u1ea3n l\u00fd Key API Check Count'}</h1>
            <p style={{ margin: 0, color: '#6b7280' }}>{'Qu\u1ea3n l\u00fd key l\u1ea5y count m\u1ea1ng x\u00e3 h\u1ed9i v\u00e0 credit theo n\u1ec1n t\u1ea3ng'}</p>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button
              onClick={() => navigate('/social-count/docs')}
              style={{
                padding: '10px 14px',
                backgroundColor: '#f8fafc',
                color: '#475569',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 600
              }}
            >
              {'T\u00e0i li\u1ec7u API'}
            </button>
            <button
              onClick={() => openLogHistory()}
              style={{
                padding: '10px 14px',
                backgroundColor: '#f8fafc',
                color: '#475569',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 600
              }}
            >
              {'L\u1ecbch s\u1eed log'}
            </button>
            <button
              onClick={() => setShowSettingsModal(true)}
              style={{
                padding: '10px 14px',
                backgroundColor: '#eef2ff',
                color: '#3730a3',
                border: '1px solid #c7d2fe',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 600
              }}
            >
              Cấu hình dịch vụ
            </button>
            <button
              onClick={openCreate}
              disabled={!hasPermission('social_count', 'create')}
              style={{
                padding: '10px 16px',
                backgroundColor: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 600
              }}
            >
              {'+ T\u1ea1o API Key'}
            </button>
          </div>
        </div>

        {notification && (
          <div style={{
            padding: '12px 16px',
            marginBottom: '16px',
            borderRadius: '8px',
            color: notification.type === 'success' ? '#065f46' : '#991b1b',
            backgroundColor: notification.type === 'success' ? '#d1fae5' : '#fee2e2',
            border: `1px solid ${notification.type === 'success' ? '#a7f3d0' : '#fecaca'}`
          }}>
            {notification.message}
          </div>
        )}

        {error && (
          <div style={{ padding: '12px 16px', marginBottom: '16px', borderRadius: '8px', color: '#991b1b', backgroundColor: '#fee2e2' }}>
            {error}
          </div>
        )}

        <div style={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: '32px', textAlign: 'center', color: '#6b7280' }}>{'\u0110ang t\u1ea3i...'}</div>
          ) : keys.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <div style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>{'Ch\u01b0a c\u00f3 key check count'}</div>
              <div style={{ color: '#6b7280' }}>{'T\u1ea1o key ri\u00eang cho Social Count API, kh\u00f4ng d\u00f9ng chung trang WhatsApp.'}</div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ backgroundColor: '#f9fafb' }}>
                  <tr>
                    <th style={thStyle}>{'T\u00ean key'}</th>
                    <th style={thStyle}>{'API key'}</th>
                    <th style={thStyle}>{'Credit / quy\u1ec1n'}</th>
                    <th style={thStyle}>{'Gi\u1edbi h\u1ea1n t\u1ea7n su\u1ea5t'}</th>
                    <th style={thStyle}>{'Tr\u1ea1ng th\u00e1i'}</th>
                    <th style={thStyle}>{'L\u01b0\u1ee3t g\u1ecdi'}</th>
                    <th style={thStyle}>{'S\u1eed d\u1ee5ng cu\u1ed1i'}</th>
                    <th style={thStyle}>{'Thao t\u00e1c'}</th>
                  </tr>
                </thead>
                <tbody>
                  {keys.map(key => {
                    const visible = visibleKeys.has(key.id);
                    return (
                      <tr key={key.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                        <td style={tdStyle}>
                          <div style={{ fontWeight: 600, color: '#111827' }}>{key.keyName}</div>
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>{formatDate(key.expiresAt)}</div>
                          <div style={{ marginTop: '6px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            <span style={getKeyTypeBadgeStyle(key.keyType)}>{formatKeyTypeName(key.keyType)}</span>
                            {key.workerLabel && <span style={{ ...statusBadgeStyle, backgroundColor: '#f3f4f6', color: '#374151' }}>{key.workerLabel}</span>}
                          </div>
                        </td>
                        <td style={tdStyle}>
                          <code style={{ fontSize: '12px', color: '#374151' }}>
                            {visible ? key.apiKey : `${key.apiKey.slice(0, 8)}................`}
                          </code>
                          <div style={{ marginTop: '6px', display: 'flex', gap: '6px' }}>
                            <button style={smallButtonStyle} onClick={() => toggleKeyVisibility(key.id)}>
                              {visible ? '\u1ea8n' : 'Hi\u1ec7n'}
                            </button>
                            <button style={smallButtonStyle} onClick={() => copyToClipboard(key.apiKey)}>
                              {'Sao ch\u00e9p'}
                            </button>
                          </div>
                        </td>
                        <td style={tdStyle}>
                          {key.keyType !== 'user' ? (
                            <span style={{ color: '#374151' }}>
                              {key.keyType === 'worker'
                                ? 'Quy\u1ec1n worker: l\u1ea5y job v\u00e0 c\u1eadp nh\u1eadt k\u1ebft qu\u1ea3'
                                : 'To\u00e0n quy\u1ec1n: API kh\u00e1ch, worker v\u00e0 xem job'}
                            </span>
                          ) : key.limits.length === 0 ? (
                            <span style={{ color: '#6b7280' }}>{'Ch\u01b0a c\u1ea5u h\u00ecnh n\u1ec1n t\u1ea3ng'}</span>
                          ) : key.limits.map(limit => (
                            <div key={limit.platform} style={{ marginBottom: '8px' }}>
                              <div>
                                <span style={{ fontWeight: 600 }}>{formatPlatformName(limit.platform)}</span>
                                <span style={{ color: '#6b7280' }}> {'\u0111\u00e3 d\u00f9ng'} {limit.creditUsed.toLocaleString()} / {formatLimit(limit.creditLimit)}</span>
                              </div>
                              <div style={{ fontSize: '12px', color: limit.isEnabled ? '#059669' : '#dc2626' }}>
                                {limit.isEnabled ? 'B\u1eadt' : 'T\u1eaft'} {' - c\u00f2n l\u1ea1i'} {formatLimit(limit.remaining)}
                              </div>
                              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                {'D\u1ecbch v\u1ee5'}: {formatServices(limit.allowedServices)}
                              </div>
                            </div>
                          ))}
                        </td>
                        <td style={tdStyle}>{formatLimit(key.rateLimit)}/{'gi\u1edd'}</td>
                        <td style={tdStyle}>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '999px',
                            backgroundColor: key.isActive ? '#d1fae5' : '#fee2e2',
                            color: key.isActive ? '#065f46' : '#991b1b',
                            fontSize: '12px',
                            fontWeight: 600
                          }}>
                            {key.isActive ? 'Ho\u1ea1t \u0111\u1ed9ng' : 'T\u1eaft'}
                          </span>
                        </td>
                        <td style={tdStyle}>{key.requestCount.toLocaleString()}</td>
                        <td style={tdStyle}>{key.lastUsed ? new Date(key.lastUsed).toLocaleString('vi-VN') : 'Ch\u01b0a s\u1eed d\u1ee5ng'}</td>
                        <td style={tdStyle}>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button style={{ ...iconButtonStyle, backgroundColor: '#eef2ff', color: '#3730a3' }} onClick={() => openLogHistory(key)} title={'L\u1ecbch s\u1eed log'}>{'Log'}</button>
                            <button style={iconButtonStyle} disabled={!hasPermission('social_count', 'update')} onClick={() => openEdit(key)} title={'S\u1eeda'}>{'\u270f'}</button>
                            <button
                              style={{ ...iconButtonStyle, backgroundColor: '#ecfeff', color: '#0e7490' }}
                              disabled={!hasPermission('social_count', 'update') || regeneratingKeyId === key.id}
                              onClick={() => handleRegenerate(key)}
                              title={'T\u1ea1o l\u1ea1i key'}
                            >
                              {regeneratingKeyId === key.id ? '...' : '\u21bb'}
                            </button>
                            <button style={{ ...iconButtonStyle, backgroundColor: '#fee2e2', color: '#991b1b' }} disabled={!hasPermission('social_count', 'delete')} onClick={() => handleDelete(key)} title={'X\u00f3a'}>{'\uD83D\uDDD1'}</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {showSettingsModal && (
          <div style={overlayStyle}>
            <div style={settingsModalStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div>
                  <h2 style={{ margin: 0 }}>Cấu hình dịch vụ Check Count</h2>
                  <div style={{ color: '#6b7280', fontSize: '13px', marginTop: '4px' }}>Bật/tắt dịch vụ, cấu hình credit và khả năng cache/fresh check.</div>
                </div>
                <button style={closeButtonStyle} onClick={() => setShowSettingsModal(false)}>x</button>
              </div>

              <div style={{ marginTop: '16px' }}>
                {renderServiceConfigPanel()}
              </div>
            </div>
          </div>
        )}

        {showLogModal && (
          <div style={overlayStyle}>
            <div style={logModalStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div>
                  <h2 style={{ margin: 0 }}>{'L\u1ecbch s\u1eed log Check Count'}</h2>
                  <div style={{ color: '#6b7280', fontSize: '13px', marginTop: '4px' }}>{'Mỗi request là một dòng; bấm Xem để xem các step xử lý bên trong. Chỉ hiển thị 30 ngày gần nhất, mỗi trang 50 request.'}</div>
                </div>
                <button style={closeButtonStyle} onClick={() => setShowLogModal(false)}>x</button>
              </div>

              <form onSubmit={submitLogFilters} style={logFilterGridStyle}>
                <label style={labelStyle}>
                  Key
                  <select value={logFilters.keyId} onChange={e => updateLogFilters({ keyId: e.target.value }, true)} style={inputStyle}>
                    <option value="">{'T\u1ea5t c\u1ea3 key'}</option>
                    {keys.map(key => <option key={key.id} value={key.id}>{key.keyName}</option>)}
                  </select>
                </label>
                <label style={labelStyle}>
                  Request ID
                  <input value={logFilters.requestId} onChange={e => updateLogFilters({ requestId: e.target.value })} style={inputStyle} placeholder="cnt_..." />
                </label>
                <label style={labelStyle}>
                  {'Ghi ch\u00fa'}
                  <input value={logFilters.note} onChange={e => updateLogFilters({ note: e.target.value })} style={inputStyle} placeholder="ORDER_12345" />
                </label>
                <label style={labelStyle}>
                  {'Tr\u1ea1ng th\u00e1i request'}
                  <select value={logFilters.requestStatus} onChange={e => updateLogFilters({ requestStatus: e.target.value }, true)} style={inputStyle}>
                    <option value="">{'T\u1ea5t c\u1ea3'}</option>
                    <option value="completed">{'Ho\u00e0n th\u00e0nh'}</option>
                    <option value="failed">{'Th\u1ea5t b\u1ea1i'}</option>
                    <option value="cache_miss">Cache miss</option>
                    <option value="rejected">{'T\u1eeb ch\u1ed1i'}</option>
                    <option value="processing">{'\u0110ang x\u1eed l\u00fd'}</option>
                  </select>
                </label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'end' }}>
                  <button type="submit" style={primaryButtonStyle}>{'T\u1ea3i log'}</button>
                  <button type="button" style={secondaryButtonStyle} onClick={() => fetchLogs(logPage, logFilters)} disabled={logLoading}>{'L\u00e0m m\u1edbi'}</button>
                </div>
              </form>

              {logError && <div style={{ padding: '10px 12px', backgroundColor: '#fee2e2', color: '#991b1b', borderRadius: '8px', marginBottom: '12px' }}>{logError}</div>}

              <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'auto', maxHeight: '58vh' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '980px' }}>
                  <thead style={{ backgroundColor: '#f9fafb', position: 'sticky', top: 0 }}>
                    <tr>
                      <th style={thStyle}>{'Thời gian'}</th>
                      <th style={thStyle}>Key</th>
                      <th style={thStyle}>Request</th>
                      <th style={thStyle}>{'Nền tảng'}</th>
                      <th style={thStyle}>{'Nguồn'}</th>
                      <th style={thStyle}>Count</th>
                      <th style={thStyle}>{'Trạng thái'}</th>
                      <th style={thStyle}>{'Chi tiết'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logLoading ? (
                      <tr><td style={tdStyle} colSpan={8}>{'Đang tải log...'}</td></tr>
                    ) : visibleLogRows.length === 0 ? (
                      <tr><td style={tdStyle} colSpan={8}>{'Chưa có log phù hợp.'}</td></tr>
                    ) : visibleLogRows.map(row => (
                      <tr key={row.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                        <td style={tdStyle}>{formatLogTime(row.createdAt)}</td>
                        <td style={tdStyle}>{row.keyName || '-'}</td>
                        <td style={tdStyle}>
                          <div style={{ fontFamily: 'monospace', fontSize: '12px' }}>{row.id}</div>
                          <div style={{ color: '#6b7280', fontSize: '12px' }}>{row.note || '-'}</div>
                        </td>
                        <td style={tdStyle}>{row.platform || '-'} / {row.service || '-'}</td>
                        <td style={tdStyle}>{row.source ? <span style={sourceBadgeStyle}>{formatSourceName(row.source)}</span> : '-'}</td>
                        <td style={tdStyle}>{row.count !== null && row.count !== undefined ? String(row.count) : '-'}</td>
                        <td style={tdStyle}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                            <span style={getRequestStatusBadgeStyle(row.status)}>{formatRequestStatusName(row.status)}</span>
                            {row.errorCode && <span style={{ color: '#991b1b', fontSize: '12px' }}>{row.errorCode}</span>}
                          </div>
                        </td>
                        <td style={tdStyle}>
                          <details>
                            <summary style={{ cursor: 'pointer', color: '#2563eb' }}>Xem {row.steps.length} step</summary>
                            <div style={{ marginTop: '8px', border: '1px solid #e5e7eb', borderRadius: '6px', overflow: 'auto', minWidth: '520px' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead style={{ backgroundColor: '#f9fafb' }}>
                                  <tr>
                                    <th style={configThStyle}>{'Thời gian'}</th>
                                    <th style={configThStyle}>Step</th>
                                    <th style={configThStyle}>{'Nguồn'}</th>
                                    <th style={configThStyle}>Level</th>
                                    <th style={configThStyle}>ms</th>
                                    <th style={configThStyle}>Data</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {row.steps.length === 0 ? (
                                    <tr><td style={configTdStyle} colSpan={6}>{'Không có step'}</td></tr>
                                  ) : row.steps.map(step => (
                                    <tr key={step.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                                      <td style={configTdStyle}>{formatLogTime(step.createdAt)}</td>
                                      <td style={{ ...configTdStyle, fontFamily: 'monospace', fontSize: '12px' }}>{step.step}</td>
                                      <td style={configTdStyle}>{step.source ? formatSourceName(step.source) : '-'}</td>
                                      <td style={configTdStyle}><span style={{ ...statusBadgeStyle, backgroundColor: step.level === 'error' ? '#fee2e2' : step.level === 'warn' ? '#fef3c7' : '#dbeafe', color: step.level === 'error' ? '#991b1b' : step.level === 'warn' ? '#92400e' : '#1e40af' }}>{step.level}</span></td>
                                      <td style={configTdStyle}>{step.elapsedMs ?? '-'}</td>
                                      <td style={configTdStyle}>
                                        <details>
                                          <summary style={{ cursor: 'pointer', color: '#2563eb' }}>JSON</summary>
                                          <pre style={logDetailsStyle}>{formatLogDetails(step.details)}</pre>
                                        </details>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </details>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' }}>
                <div style={{ color: '#6b7280', fontSize: '13px' }}>Trang {logPage}</div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button style={secondaryButtonStyle} disabled={logLoading || logPage <= 1} onClick={() => fetchLogs(logPage - 1, logFilters)}>{'Trang tr\u01b0\u1edbc'}</button>
                  <button style={secondaryButtonStyle} disabled={logLoading || !logHasNext} onClick={() => fetchLogs(logPage + 1, logFilters)}>Trang sau</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showModal && (
          <div style={overlayStyle}>
            <div style={modalStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ margin: 0 }}>{newApiKey ? (newApiKeyTitle || 'API key m\u1edbi') : editingKey ? 'S\u1eeda Key API Check Count' : 'T\u1ea1o Key API Check Count'}</h2>
                <button style={closeButtonStyle} onClick={() => { setShowModal(false); resetForm(); }}>x</button>
              </div>

              {newApiKey ? (
                <div>
                  <p style={{ color: '#374151' }}>{'\u0110\u00e2y l\u00e0 l\u1ea7n hi\u1ec3n th\u1ecb \u0111\u1ea7y \u0111\u1ee7 API key. H\u00e3y sao ch\u00e9p \u0111\u1ec3 ki\u1ec3m tra.'}</p>
                  <div style={{ padding: '12px', backgroundColor: '#f3f4f6', borderRadius: '8px', wordBreak: 'break-all', marginBottom: '16px' }}>
                    <code>{newApiKey}</code>
                  </div>
                  <button style={primaryButtonStyle} onClick={() => copyToClipboard(newApiKey)}>{'Sao ch\u00e9p API key'}</button>
                  <button style={{ ...secondaryButtonStyle, marginLeft: '8px' }} onClick={() => { setShowModal(false); resetForm(); }}>{'\u0110\u00f3ng'}</button>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div style={formGridStyle}>
                    <label style={labelStyle}>
                      {'T\u00ean key'}
                      <input value={form.keyName} onChange={e => setForm({ ...form, keyName: e.target.value })} style={inputStyle} placeholder={'VD: Kh\u00e1ch A Instagram Count'} />
                    </label>

                    <label style={labelStyle}>
                      {'Loại key'}
                      <select
                        value={form.keyType}
                        onChange={e => {
                          const nextType = e.target.value as SocialKeyType;
                          setForm({
                            ...form,
                            keyType: nextType,
                            workerLabel: nextType === 'user' ? '' : form.workerLabel,
                            rateNoLimit: nextType === 'user' ? form.rateNoLimit : true,
                            expiryNoLimit: nextType === 'user' ? form.expiryNoLimit : true
                          });
                        }}
                        style={inputStyle}
                      >
                        <option value="user">{'Kh\u00e1ch h\u00e0ng'}</option>
                        <option value="worker">Worker</option>
                        <option value="admin">Admin</option>
                      </select>
                    </label>

                    {form.keyType !== 'user' && (
                      <label style={labelStyle}>
                        {'Nh\u00e3n worker'}
                        <input value={form.workerLabel} onChange={e => setForm({ ...form, workerLabel: e.target.value })} style={inputStyle} placeholder={'VD: pc-worker-01'} />
                        <span style={{ color: '#6b7280', fontSize: '12px', fontWeight: 400 }}>{form.keyType === 'worker' ? 'D\u00f9ng l\u00e0m workerId m\u1eb7c \u0111\u1ecbnh khi worker kh\u00f4ng g\u1eedi workerId.' : 'T\u00f9y ch\u1ecdn, d\u00f9ng khi admin key g\u1ecdi endpoint worker.'}</span>
                      </label>
                    )}

                    {form.keyType === 'user' && renderPlatformLimits()}

                    <label style={labelStyle}>
                      {'Gi\u1edbi h\u1ea1n request / gi\u1edd'}
                      <input type="number" value={form.rateNoLimit ? '' : form.rateLimit} disabled={form.rateNoLimit} onChange={e => setForm({ ...form, rateLimit: Number(e.target.value) })} style={inputStyle} />
                      <label style={checkStyle}>
                        <input type="checkbox" checked={form.rateNoLimit} onChange={e => setForm({ ...form, rateNoLimit: e.target.checked })} />
                        {'Kh\u00f4ng gi\u1edbi h\u1ea1n'}
                      </label>
                    </label>

                    <label style={labelStyle}>
                      {'Th\u1eddi h\u1ea1n (ng\u00e0y)'}
                      <input type="number" value={form.expiryNoLimit ? '' : form.expiryDays} disabled={form.expiryNoLimit} onChange={e => setForm({ ...form, expiryDays: Number(e.target.value) })} style={inputStyle} />
                      <label style={checkStyle}>
                        <input type="checkbox" checked={form.expiryNoLimit} onChange={e => setForm({ ...form, expiryNoLimit: e.target.checked })} />
                        {'Kh\u00f4ng th\u1eddi h\u1ea1n'}
                      </label>
                    </label>

                    <label style={labelStyle}>
                      {'Device ID t\u00f9y ch\u1ecdn'}
                      <input value={form.deviceId} onChange={e => setForm({ ...form, deviceId: e.target.value })} style={inputStyle} placeholder={'B\u1ecf tr\u1ed1ng n\u1ebfu ch\u1ec9 d\u00f9ng API key'} />
                    </label>

                    <label style={{ ...labelStyle, gridColumn: '1 / -1' }}>
                      {'T\u00ean mi\u1ec1n cho ph\u00e9p (t\u00f9y ch\u1ecdn)'}
                      <textarea value={form.allowedDomain} onChange={e => setForm({ ...form, allowedDomain: e.target.value })} style={{ ...inputStyle, minHeight: '74px' }} placeholder="example.com&#10;api.example.com" />
                    </label>

                    <label style={checkStyle}>
                      <input type="checkbox" checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} />
                      {'Key \u0111ang ho\u1ea1t \u0111\u1ed9ng'}
                    </label>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '24px' }}>
                    <button type="button" style={secondaryButtonStyle} onClick={() => { setShowModal(false); resetForm(); }}>{'H\u1ee7y'}</button>
                    <button type="submit" style={primaryButtonStyle}>{editingKey ? 'L\u01b0u' : 'T\u1ea1o key'}</button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

const configThStyle: React.CSSProperties = {
  padding: '8px 10px',
  textAlign: 'left',
  fontSize: '11px',
  color: '#6b7280',
  fontWeight: 700,
  textTransform: 'uppercase',
  whiteSpace: 'nowrap'
};

const configTdStyle: React.CSSProperties = {
  padding: '10px',
  fontSize: '13px',
  color: '#374151',
  verticalAlign: 'middle'
};

const compactInputStyle: React.CSSProperties = {
  padding: '6px 8px',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  fontSize: '13px'
};

const thStyle: React.CSSProperties = {
  padding: '12px',
  textAlign: 'left',
  fontSize: '12px',
  color: '#6b7280',
  fontWeight: 700,
  textTransform: 'uppercase'
};

const tdStyle: React.CSSProperties = {
  padding: '14px 12px',
  fontSize: '14px',
  color: '#374151',
  verticalAlign: 'top'
};

const smallButtonStyle: React.CSSProperties = {
  padding: '4px 8px',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  backgroundColor: '#f9fafb',
  cursor: 'pointer',
  fontSize: '12px'
};

const iconButtonStyle: React.CSSProperties = {
  padding: '7px 9px',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  backgroundColor: '#f9fafb',
  cursor: 'pointer'
};

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(17, 24, 39, 0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 2000,
  padding: '20px'
};

const modalStyle: React.CSSProperties = {
  width: 'min(1120px, 100%)',
  maxHeight: '90vh',
  overflow: 'auto',
  backgroundColor: 'white',
  borderRadius: '8px',
  padding: '24px',
  boxShadow: '0 20px 45px rgba(0,0,0,0.18)'
};

const settingsModalStyle: React.CSSProperties = {
  width: 'min(1180px, 100%)',
  maxHeight: '92vh',
  overflow: 'auto',
  backgroundColor: 'white',
  borderRadius: '8px',
  padding: '24px',
  boxShadow: '0 20px 45px rgba(0,0,0,0.18)'
};

const logModalStyle: React.CSSProperties = {
  width: 'min(1500px, 100%)',
  maxHeight: '92vh',
  overflow: 'auto',
  backgroundColor: 'white',
  borderRadius: '8px',
  padding: '24px',
  boxShadow: '0 20px 45px rgba(0,0,0,0.18)'
};

const logFilterGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
  gap: '12px',
  alignItems: 'end',
  marginBottom: '14px'
};

const logDetailsStyle: React.CSSProperties = {
  maxWidth: '420px',
  maxHeight: '220px',
  overflow: 'auto',
  padding: '10px',
  borderRadius: '6px',
  backgroundColor: '#f8fafc',
  color: '#334155',
  fontSize: '12px',
  whiteSpace: 'pre-wrap'
};

const closeButtonStyle: React.CSSProperties = {
  border: 'none',
  backgroundColor: 'transparent',
  fontSize: '28px',
  cursor: 'pointer',
  color: '#6b7280'
};

const formGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
  gap: '16px'
};

const platformGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
  gap: '12px',
  marginTop: '12px'
};

const platformCardStyle: React.CSSProperties = {
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  padding: '14px',
  backgroundColor: '#f9fafb'
};

const platformControlsStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(160px, 1fr) auto',
  gap: '12px',
  marginTop: '12px'
};

const servicePillStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '6px 9px',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  backgroundColor: 'white',
  fontSize: '13px',
  fontWeight: 500
};

const requestStatusBadgeStyle: React.CSSProperties = {
  padding: '4px 8px',
  borderRadius: '999px',
  fontSize: '12px',
  fontWeight: 700,
  whiteSpace: 'nowrap'
};

const serviceConfigPanelStyle: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '8px',
  border: '1px solid #e5e7eb',
  padding: '16px',
  marginBottom: '18px'
};

const enabledProviderBadgeStyle: React.CSSProperties = {
  padding: '2px 6px',
  borderRadius: '999px',
  backgroundColor: '#d1fae5',
  color: '#065f46',
  fontSize: '11px',
  fontWeight: 700
};

const disabledProviderBadgeStyle: React.CSSProperties = {
  padding: '2px 6px',
  borderRadius: '999px',
  backgroundColor: '#fee2e2',
  color: '#991b1b',
  fontSize: '11px',
  fontWeight: 700
};

const sourceBadgeStyle: React.CSSProperties = {
  padding: '4px 8px',
  borderRadius: '999px',
  backgroundColor: '#eef2ff',
  color: '#3730a3',
  fontSize: '12px',
  fontWeight: 700,
  display: 'inline-block',
  whiteSpace: 'nowrap'
};

const statusBadgeStyle: React.CSSProperties = {
  padding: '2px 6px',
  borderRadius: '999px',
  backgroundColor: '#fef3c7',
  color: '#92400e',
  fontSize: '11px',
  fontWeight: 700,
  whiteSpace: 'nowrap'
};

const labelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  fontSize: '14px',
  fontWeight: 600,
  color: '#374151'
};

const inputStyle: React.CSSProperties = {
  padding: '10px 12px',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  fontSize: '14px'
};

const checkStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: '13px',
  color: '#374151',
  fontWeight: 500
};

const primaryButtonStyle: React.CSSProperties = {
  padding: '10px 16px',
  border: 'none',
  borderRadius: '8px',
  backgroundColor: '#2563eb',
  color: 'white',
  cursor: 'pointer',
  fontWeight: 700
};

const secondaryButtonStyle: React.CSSProperties = {
  padding: '10px 16px',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  backgroundColor: 'white',
  color: '#374151',
  cursor: 'pointer',
  fontWeight: 600
};

export default SocialCountKeys;
